import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getUserTeams, 
  getInvoicesForReport, 
  formatCurrency,
  formatDate,
  getCurrentFactorValue
} from '../services/firebaseService';
import { 
  Team, 
  Invoice, 
  DateRange 
} from '../types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './DailyReport.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DailyData {
  team: string;
  totalValue: number;
  totalInvoices: number;
  checkedInvoices: number;
  pendingInvoices: number;
  viewedInvoices: number;
  viewedValue: number;
  workDays: number;
  dailyAverage: number;
  goalStatus: 'met' | 'above' | 'below';
  maxGoalStatus: 'met' | 'above' | 'below';
}


const DailyReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });
  const [dailyGoal, setDailyGoal] = useState(5000);
  const [maxGoal, setMaxGoal] = useState(120000);

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    
    const startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-26`;
    const endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-25`;

    setDateRange({ startDate, endDate });
  };

  const getWorkDays = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date();
    let workDays = 0;
    let current = new Date(start);

    while (current <= end && current <= new Date(endDate)) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workDays++;
      } else if (dayOfWeek === 6) {
        workDays += 0.5;
      }
      current.setDate(current.getDate() + 1);
    }

    return workDays;
  };

  const calculateTotalFromServices = (services: any[]): number => {
    if (!services || !Array.isArray(services)) return 0;
    return services.reduce((total, service) => {
      if (service) {
        const quantity = parseFloat(service.quantity?.toString() || '0');
        const price = parseFloat(service.price?.toString() || '0');
        const parsed = parseFloat(service.factor?.toString() || '');
        const factor = !isNaN(parsed) && parsed > 0 ? parsed : getCurrentFactorValue();
        return total + (quantity * price * factor);
      }
      return total;
    }, 0);
  };

  const loadReportData = async () => {
    if (selectedTeams.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      alert('Selecione pelo menos uma equipe e defina o período.');
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, dateRange.startDate, dateRange.endDate);
        allInvoices.push(...teamInvoices);
      }

      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error loading report data:', error);
      alert('Erro ao carregar dados do relatório.');
    } finally {
      setLoading(false);
    }
  };

  const dailyReportData = useMemo(() => {
    const workDays = getWorkDays(dateRange.startDate, dateRange.endDate);
    let teamGoal = workDays * dailyGoal;
    if (teamGoal > maxGoal) {
      teamGoal = maxGoal;
    }

    const teamData: { [key: string]: DailyData } = {};

    selectedTeams.forEach(teamId => {
      const teamName = teams.find(t => t.id === teamId)?.name || teamId;
      const teamInvoices = invoices.filter(inv => inv.teamId === teamId || inv.team === teamId);
      
      const totalValue = teamInvoices.reduce((sum, inv) => 
        sum + calculateTotalFromServices(inv.services || []), 0);
      const checkedInvoices = teamInvoices.filter(inv => inv.checked).length;
      const viewedInvoices = teamInvoices.filter(inv => inv.viewedByFat || inv.status === 'viewed').length;
      const viewedValue = teamInvoices
        .filter(inv => inv.viewedByFat || inv.status === 'viewed')
        .reduce((sum, inv) => sum + calculateTotalFromServices(inv.services || []), 0);
      const totalInvoices = teamInvoices.length;
      const dailyAverage = workDays > 0 ? totalValue / workDays : 0;

      let goalStatus: 'met' | 'above' | 'below' = 'below';
      if (totalValue >= teamGoal) {
        goalStatus = totalValue > teamGoal ? 'above' : 'met';
      }

      // Compare the team's faturamento (totalValue) against the Meta Máxima
      let maxGoalStatus: 'met' | 'above' | 'below' = 'below';
      if (totalValue >= maxGoal) {
        maxGoalStatus = totalValue > maxGoal ? 'above' : 'met';
      }

      teamData[teamId] = {
        team: teamName,
        totalValue,
        totalInvoices,
        checkedInvoices,
        pendingInvoices: totalInvoices - checkedInvoices,
        viewedInvoices,
        viewedValue,
        workDays,
        dailyAverage,
        goalStatus,
        maxGoalStatus
      };
    });

    return Object.values(teamData);
  }, [invoices, selectedTeams, teams, dateRange, dailyGoal, maxGoal]);

  const reportSummary = useMemo(() => {
    const totalInvoices = dailyReportData.reduce((sum, team) => sum + team.totalInvoices, 0);
    const totalChecked = dailyReportData.reduce((sum, team) => sum + team.checkedInvoices, 0);
    const totalViewed = dailyReportData.reduce((sum, team) => sum + team.viewedInvoices, 0);
    const totalViewedValue = dailyReportData.reduce((sum, team) => sum + team.viewedValue, 0);
    const totalValue = dailyReportData.reduce((sum, team) => sum + team.totalValue, 0);
    const teamsInGoal = dailyReportData.filter(team => team.goalStatus === 'met' || team.goalStatus === 'above').length;
    const teamsAboveGoal = dailyReportData.filter(team => team.goalStatus === 'above').length;
    const teamsOutGoal = dailyReportData.filter(team => team.goalStatus === 'below').length;
    
    // Calculate current goal based on work days
    const workDays = dailyReportData.length > 0 ? dailyReportData[0].workDays : 0;
    const currentGoal = Math.min(workDays * dailyGoal, maxGoal);

    return {
      totalInvoices,
      totalChecked,
      totalViewed,
      totalViewedValue,
      totalPending: totalInvoices - totalChecked,
      totalValue,
      teamsInGoal,
      teamsAboveGoal,
      teamsOutGoal,
      currentGoal,
      workDays
    };
  }, [dailyReportData, dailyGoal, maxGoal]);

  const sortedTeamData = useMemo(() => {
    return [...dailyReportData].sort((a, b) => b.totalValue - a.totalValue);
  }, [dailyReportData]);

  const dailyChartData = useMemo(() => {
    // Create a structure to hold daily totals per team
    const dailyTeamTotals: { [date: string]: { [team: string]: { totalValue: number; invoiceCount: number } } } = {};
    const allDates = new Set<string>();
    
    invoices.forEach(invoice => {
      const invoiceDate = formatDate(invoice.date) || invoice.header?.date || '';
      const dateKey = invoiceDate.split('/').slice(0, 2).join('/'); // DD/MM format
      const teamId = invoice.teamId || invoice.team || 'Unknown';
      const teamName = teams.find(t => t.id === teamId)?.name || teamId;
      
      const totalValue = calculateTotalFromServices(invoice.services || []);
      
      if (!dailyTeamTotals[dateKey]) {
        dailyTeamTotals[dateKey] = {};
      }
      
      if (!dailyTeamTotals[dateKey][teamName]) {
        dailyTeamTotals[dateKey][teamName] = {
          totalValue: 0,
          invoiceCount: 0
        };
      }
      
      dailyTeamTotals[dateKey][teamName].totalValue += totalValue;
      dailyTeamTotals[dateKey][teamName].invoiceCount += 1;
      allDates.add(dateKey);
    });

    // Sort dates
    const sortedDates = Array.from(allDates).sort((a, b) => {
      const [dayA, monthA] = a.split('/').map(Number);
      const [dayB, monthB] = b.split('/').map(Number);
      
      if (monthA !== monthB) {
        return monthA - monthB;
      }
      return dayA - dayB;
    });

    return {
      dates: sortedDates,
      teamData: dailyTeamTotals
    };
  }, [invoices, teams]);

  const chartData = useMemo(() => {
    // Get all unique team names from selected teams
    const selectedTeamNames = selectedTeams.map(teamId => 
      teams.find(t => t.id === teamId)?.name || teamId
    );

    // Color palette for teams
    const colors = [
      'rgba(49, 130, 206, 0.8)',   // Blue
      'rgba(56, 161, 105, 0.8)',   // Green
      'rgba(237, 137, 54, 0.8)',   // Orange
      'rgba(229, 62, 62, 0.8)',    // Red
      'rgba(128, 90, 213, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(14, 165, 233, 0.8)',   // Sky
      'rgba(34, 197, 94, 0.8)',    // Emerald
      'rgba(251, 146, 60, 0.8)',   // Amber
      'rgba(168, 85, 247, 0.8)',   // Violet
    ];

    const datasets = selectedTeamNames.map((teamName, index) => ({
      label: teamName,
      data: dailyChartData.dates.map(date => 
        dailyChartData.teamData[date]?.[teamName]?.totalValue || 0
      ),
      backgroundColor: colors[index % colors.length],
      borderColor: colors[index % colors.length].replace('0.8', '1'),
      borderWidth: 2,
      borderRadius: 4,
      borderSkipped: false,
    }));

    return {
      labels: dailyChartData.dates,
      datasets,
    };
  }, [dailyChartData, selectedTeams, teams]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Faturamento Diário por Equipe',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        padding: 20,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const teamName = context.dataset.label;
            const date = context.label;
            const value = context.parsed.y;
            const invoiceCount = dailyChartData.teamData[date]?.[teamName]?.invoiceCount || 0;
            
            return [
              `${teamName}: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
              `Boletins: ${invoiceCount}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Data',
          font: {
            size: 12,
            weight: 'bold' as const,
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'Valor (R$)',
          font: {
            size: 12,
            weight: 'bold' as const,
          }
        },
        ticks: {
          callback: function(value: any) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        stacked: false
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map(team => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const exportToCSV = () => {
    const headers = [
      'Equipe', 'Faturamento Total', 'Total de Boletins', 'Boletins Liberados', 
      'Valor Visualizado', 'Boletins Pendentes', 'Dias Trabalhados', 'Média Diária', 'Status da Meta', 'Status Meta Máxima'
    ].join(';');

    const csvData = sortedTeamData.map(team => [
      `"${team.team}"`,
      team.totalValue.toFixed(2),
      team.totalInvoices.toString(),
      team.checkedInvoices.toString(),
      team.viewedValue.toFixed(2),
      team.pendingInvoices.toString(),
      team.workDays.toString(),
      team.dailyAverage.toFixed(2),
      team.goalStatus === 'met' ? 'Na Meta' : team.goalStatus === 'above' ? 'Acima da Meta' : 'Fora da Meta',
      team.maxGoalStatus === 'met' ? 'Na Máxima' : team.maxGoalStatus === 'above' ? 'Acima da Máxima' : 'Fora da Máxima'
    ].join(';')).join('\n');

    const csvContent = [headers, csvData].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-diario-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status: 'met' | 'above' | 'below') => {
    switch (status) {
      case 'above':
        return <span className="badge badge-success">Acima da Meta</span>;
      case 'met':
        return <span className="badge badge-primary">Na Meta</span>;
      case 'below':
        return <span className="badge badge-danger">Fora da Meta</span>;
    }
  };

  return (
    <div className="daily-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Diário</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button onClick={handleDeselectAllTeams} className="control-btn">
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período de Faturamento</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Configurações de Meta</h3>
            <p className="goal-description">
              A meta final é calculada como: <strong>Meta Diária × Dias Trabalhados</strong>, limitada pela Meta Máxima
            </p>
            <div className="goal-inputs">
              <div className="goal-input-group">
                <label htmlFor="dailyGoal">Meta Diária (R$):</label>
                <input
                  id="dailyGoal"
                  type="number"
                  value={dailyGoal}
                  onChange={(e) => setDailyGoal(Number(e.target.value) || 0)}
                  className="goal-input"
                  min="0"
                  step="100"
                  placeholder="5000"
                />
              </div>
              <div className="goal-input-group">
                <label htmlFor="maxGoal">Meta Máxima (R$):</label>
                <input
                  id="maxGoal"
                  type="number"
                  value={maxGoal}
                  onChange={(e) => setMaxGoal(Number(e.target.value) || 0)}
                  className="goal-input"
                  min="0"
                  step="1000"
                  placeholder="120000"
                />
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-chart-bar"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {dailyReportData.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {dailyReportData.length > 0 && (
          <div className="summary-section">
            <h3>Resumo do Período</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalInvoices}</div>
                <div className="card-label">Total de Boletins</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalChecked}</div>
                <div className="card-label">Boletins Liberados</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.totalViewedValue)}</div>
                <div className="card-label">Valor Visualizado</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalPending}</div>
                <div className="card-label">Boletins Pendentes</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.totalValue)}</div>
                <div className="card-label">Faturamento Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.teamsInGoal + reportSummary.teamsAboveGoal}</div>
                <div className="card-label">Equipes na Meta</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.teamsOutGoal}</div>
                <div className="card-label">Equipes Fora da Meta</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.currentGoal)}</div>
                <div className="card-label">Meta Atual ({reportSummary.workDays} dias)</div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Chart Section */}
        {dailyChartData.dates.length > 0 && (
          <div className="chart-section">
            <div className="chart-container">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {/* Results Table */}
        {dailyReportData.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Desempenho por Equipe ({sortedTeamData.length} equipes)</h3>
            </div>

            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Ranking</th>
                    <th>Equipe</th>
                    <th>Faturamento (R$)</th>
                    <th>Boletins</th>
                    <th>Liberados</th>
                    <th>Valor Visualizado (R$)</th>
                    <th>Pendentes</th>
                    <th>Dias Trabalhados</th>
                    <th>Média Diária (R$)</th>
                    <th>Status da Meta</th>
                    <th>Status Meta Máxima</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTeamData.map((team, index) => (
                    <tr key={team.team} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td className="ranking-cell">
                        <strong>#{index + 1}</strong>
                      </td>
                      <td className="team-cell">
                        <strong>{team.team}</strong>
                      </td>
                      <td className="value-cell">
                        {formatCurrency(team.totalValue)}
                      </td>
                      <td className="number-cell">{team.totalInvoices}</td>
                      <td className="number-cell">{team.checkedInvoices}</td>
                      <td className="value-cell">{formatCurrency(team.viewedValue)}</td>
                      <td className="number-cell">{team.pendingInvoices}</td>
                      <td className="number-cell">{team.workDays}</td>
                      <td className="value-cell">
                        {formatCurrency(team.dailyAverage)}
                      </td>
                      <td className="status-cell">{getStatusBadge(team.goalStatus)}</td>
                      <td className="status-cell">{getStatusBadge(team.maxGoalStatus)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      Total Geral
                    </td>
                    <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                      {formatCurrency(reportSummary.totalValue)}
                    </td>
                    <td style={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {reportSummary.totalInvoices}
                    </td>
                    <td style={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {reportSummary.totalChecked}
                    </td>
                    <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                      {formatCurrency(reportSummary.totalViewedValue)}
                    </td>
                    <td style={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {reportSummary.totalPending}
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {dailyReportData.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-chart-bar"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Configure os filtros e clique em "Gerar Relatório" para visualizar o desempenho diário das equipes.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReport;
